import { motion, type Variants } from "framer-motion";
import { ArrowRight, ChevronRight, MapPin } from "lucide-react";
import { Mark } from "@/components/Wordmark";

const STATS = [
  { k: "Scope", v: "silicon → cloud" },
  { k: "Systems", v: "3 in production" },
  { k: "Edge", v: "air-gapped AI" },
  { k: "Posture", v: "gov-grade security" },
];

const DOSSIER = [
  { k: "Operator", v: "Chase Bradley-West" },
  { k: "Role", v: "Forward Deployed Engineer" },
  { k: "Station", v: "Newport Beach, CA" },
  { k: "Disciplines", v: "SW · Embedded · IoT · EE" },
  { k: "Clearance posture", v: "Security-first · gov use" },
];

const fade: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export function Hero() {
  return (
    <section id="top" className="relative flex min-h-screen items-center px-5 pt-28 pb-20 sm:px-8">
      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-[1.25fr_0.85fr]">
        {/* Left: statement */}
        <div className="flex flex-col gap-7">
          <motion.div custom={0} variants={fade} initial="hidden" animate="show" className="flex items-center gap-3">
            <Mark className="size-5" />
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.28em] text-ink-400">
              Chase Bradley-West <span className="text-ink-600">//</span>{" "}
              <span className="text-signal-400">Forward Deployed Engineer</span>
            </span>
          </motion.div>

          <motion.h1
            custom={1}
            variants={fade}
            initial="hidden"
            animate="show"
            className="font-display text-5xl font-bold leading-[1.04] tracking-tight text-ink-50 sm:text-6xl lg:text-[4.4rem]"
          >
            Deployed forward,
            <br />
            into the <span className="text-gradient-signal">hard problems</span>
            <br />
            others route around.
          </motion.h1>

          <motion.p
            custom={2}
            variants={fade}
            initial="hidden"
            animate="show"
            className="max-w-xl text-lg leading-relaxed text-ink-300"
          >
            I embed with your team and own the build end to end — software, embedded, IoT,
            and electrical engineering. I've shipped air-gapped, AI-empowered devices and the
            autonomous agent systems that run them, with a background in security and systems
            built for government use.
          </motion.p>

          <motion.div custom={3} variants={fade} initial="hidden" animate="show" className="flex flex-wrap items-center gap-4 pt-1">
            <a
              href="#contact"
              className="group inline-flex h-12 items-center gap-2 rounded-[var(--radius-md)] bg-signal-500 px-6 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-ink-950 shadow-[0_10px_36px_-12px_rgba(244,96,15,0.8)] transition-all hover:bg-signal-400"
            >
              Start a project
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#products"
              className="inline-flex h-12 items-center gap-2 rounded-[var(--radius-md)] border border-ink-700 px-6 font-mono text-xs uppercase tracking-[0.16em] text-ink-200 transition-colors hover:border-ink-500 hover:bg-ink-800/50"
            >
              View systems
              <ChevronRight className="size-4" />
            </a>
          </motion.div>

          {/* Stat strip */}
          <motion.div
            custom={4}
            variants={fade}
            initial="hidden"
            animate="show"
            className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-[var(--radius-md)] border border-ink-800 bg-ink-800 sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.k} className="bg-ink-950/80 px-4 py-3">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-ink-500">{s.k}</div>
                <div className="mt-1 font-display text-sm font-semibold text-ink-100">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: operator dossier */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <div className="brackets glass relative rounded-[var(--radius-lg)] p-6">
            <div className="flex items-center justify-between border-b border-ink-700/60 pb-3">
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-ink-400">
                Operator Dossier
              </span>
              <span className="flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-success">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-success" />
                </span>
                Available
              </span>
            </div>

            <dl className="mt-4 flex flex-col divide-y divide-ink-800/80">
              {DOSSIER.map((d) => (
                <div key={d.k} className="flex items-center justify-between py-2.5">
                  <dt className="font-mono text-[0.62rem] uppercase tracking-[0.16em] text-ink-500">{d.k}</dt>
                  <dd className="text-right text-sm font-medium text-ink-100">{d.v}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-ink-800 bg-ink-950/60 px-3 py-2 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-ink-400">
              <MapPin className="size-3.5 text-signal-500" />
              33.6189°N 117.9298°W
            </div>
          </div>

          {/* glow behind the card */}
          <div className="absolute inset-0 -z-10 blur-3xl">
            <div className="absolute right-0 top-1/3 size-64 rounded-full bg-signal-500/15" />
            <div className="absolute left-0 bottom-0 size-40 rounded-full bg-invert-500/10" />
          </div>
        </motion.div>
      </div>

      {/* scroll cue */}
      <div className="pointer-events-none absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex">
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-ink-600">Scroll</span>
        <span className="h-8 w-px bg-gradient-to-b from-ink-500 to-transparent" />
      </div>
    </section>
  );
}
